import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Chip,
  IconButton,
  Stack,
  Divider,
  Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import HistoryIcon from '@mui/icons-material/History';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ScienceIcon from '@mui/icons-material/Science';
import CloseIcon from '@mui/icons-material/Close';

export interface MessageDraft {
  id: string;
  patientId: string;
  patientName: string;
  draftContent: string;
  rationale?: string;
  evidenceRefs: {
    refType: 'CGM_EVENT' | 'MEAL_ENTRY' | 'CONTEXT_NOTE' | 'LAB_RESULT' | 'PROTOCOL_VERSION' | 'MESSAGE_RESPONSE';
    refId: string;
    label?: string;
  }[];
  status: 'PENDING_REVIEW' | 'APPROVED' | 'REJECTED';
  createdAt: string;
}

export interface ConversationMessage {
  id: string;
  patientId: string;
  timestamp: string;
  question: string;
  response: string;
  respondedBy: string;
  status: 'DRAFT' | 'SENT';
}

interface DraftReviewModalProps {
  open: boolean;
  selectedDraft: MessageDraft | null;
  editedContent: string;
  setEditedContent: (val: string) => void;
  isEditing: boolean;
  showConversation: boolean;
  setShowConversation: (val: boolean) => void;
  conversationMessages: ConversationMessage[];
  currentQuestion: string;
  onClose: () => void;
  onApprove: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onSend: () => void;
  onLoadMoreConversation: () => void;
  onViewRecord: () => void;
  onOrderLabs: () => void;
}

export const DraftReviewModal = ({
  open,
  selectedDraft,
  editedContent,
  setEditedContent,
  isEditing,
  showConversation,
  setShowConversation,
  conversationMessages,
  currentQuestion,
  onClose,
  onApprove,
  onEdit,
  onDelete,
  onSend,
  onLoadMoreConversation,
  onViewRecord,
  onOrderLabs,
}: DraftReviewModalProps) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h6">Review AI-Generated Draft</Typography>
          <Typography variant="body2" color="text.secondary">
            Patient: {selectedDraft?.patientName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Stack>
    </DialogTitle>

    <DialogContent dividers>
      {/* Patient Question */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Patient Question
        </Typography>
        <Typography variant="body1">{currentQuestion}</Typography>
      </Paper>

      {/* AI Response */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.50' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="subtitle2" color="primary.main">
            AI-Generated Response
          </Typography>
          <Chip label="Pending Review" color="warning" size="small" />
        </Stack>
        {isEditing ? (
          <TextField
            fullWidth
            multiline
            rows={6}
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            variant="outlined"
          />
        ) : (
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {editedContent}
          </Typography>
        )}
      </Paper>

      {/* AI Rationale */}
      {selectedDraft?.rationale && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'info.50' }}>
          <Typography variant="subtitle2" color="info.main" gutterBottom>
            AI Reasoning
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedDraft.rationale}
          </Typography>
        </Paper>
      )}

      {/* Evidence References */}
      {selectedDraft?.evidenceRefs && selectedDraft.evidenceRefs.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Evidence References
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {selectedDraft.evidenceRefs.map((ref, idx) => (
              <Chip
                key={idx}
                label={ref.label || ref.refType}
                size="small"
                variant="outlined"
                color={ref.refType === 'CGM_EVENT' ? 'success' : 'default'}
              />
            ))}
          </Stack>
        </Paper>
      )}

      {/* Conversation History */}
      <Box sx={{ mt: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="subtitle1">
            <HistoryIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Conversation History
          </Typography>
          <Button size="small" onClick={() => setShowConversation(!showConversation)}>
            {showConversation ? 'Hide' : 'Show'}
          </Button>
        </Stack>

        {showConversation && (
          <Box>
            {conversationMessages.length === 0 ? (
              <Typography variant="body2" color="text.secondary" align="center" py={2}>
                No previous conversation history
              </Typography>
            ) : (
              <Stack spacing={2}>
                {conversationMessages
                  .filter((msg) => msg.status === 'SENT')
                  .map((msg) => (
                    <Paper key={msg.id} sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(msg.timestamp).toLocaleDateString()} at{' '}
                          {new Date(msg.timestamp).toLocaleTimeString()}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          Q: {msg.question}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          A: {msg.response}
                        </Typography>
                        <Typography variant="caption" color="primary.main">
                          — {msg.respondedBy}
                        </Typography>
                      </Stack>
                    </Paper>
                  ))}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onLoadMoreConversation}
                  startIcon={<HistoryIcon />}
                >
                  Load Previous 5 Messages
                </Button>
              </Stack>
            )}
          </Box>
        )}
      </Box>
    </DialogContent>

    <DialogActions sx={{ p: 2, flexDirection: 'column', gap: 2 }}>
      {/* Action Buttons Row 1: Review Actions */}
      <Stack direction="row" spacing={1} width="100%" justifyContent="center">
        <Tooltip title="Approve and mark ready to send">
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={onApprove}
            disabled={isEditing}
          >
            Approve
          </Button>
        </Tooltip>
        <Tooltip title="Edit the AI-generated response">
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={onEdit}
            disabled={isEditing}
          >
            Edit
          </Button>
        </Tooltip>
        <Tooltip title="Delete this draft">
          <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={onDelete}>
            Delete
          </Button>
        </Tooltip>
        <Tooltip title="Send response to patient">
          <Button
            variant="contained"
            color="primary"
            startIcon={<SendIcon />}
            onClick={onSend}
          >
            Send to Patient
          </Button>
        </Tooltip>
      </Stack>

      <Divider sx={{ width: '100%' }} />

      {/* Action Buttons Row 2: Patient Actions */}
      <Stack direction="row" spacing={1} width="100%" justifyContent="center">
        <Tooltip title="View patient clinical record">
          <Button variant="outlined" startIcon={<FolderOpenIcon />} onClick={onViewRecord}>
            Clinical Record
          </Button>
        </Tooltip>
        <Tooltip title="Order lab tests for this patient">
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ScienceIcon />}
            onClick={onOrderLabs}
          >
            Order Labs
          </Button>
        </Tooltip>
      </Stack>
    </DialogActions>
  </Dialog>
);
